import type { SavedProposalDoc } from '../ProposalsModal';

export type ProposalStatus = 'draft' | 'quoted' | 'won' | 'lost';

export const STATUS_LABELS: Record<ProposalStatus, string> = {
  draft: 'Taslak',
  quoted: 'Teklif Verildi',
  won: 'Onaylandı',
  lost: 'Kaybedildi',
};

/** Tailwind background classes for the status dots/bars — mirrors the archive modal's palette. */
export const STATUS_COLORS: Record<ProposalStatus, string> = {
  draft: 'bg-stone-400',
  quoted: 'bg-indigo-400',
  won: 'bg-emerald-500',
  lost: 'bg-red-400',
};

export const STATUS_ORDER: ProposalStatus[] = ['draft', 'quoted', 'won', 'lost'];

// Thresholds behind the "Aksiyon Gerekenler" list. Deliberately conservative:
// the list is only useful while every row on it is genuinely worth a phone call.
export const EXPIRING_SOON_DAYS = 7;   // teklif geçerlilik tarihine kalan gün
export const STALE_QUOTED_DAYS = 5;    // teklif verildi, bu kadar gündür dokunulmadı
export const STALE_DRAFT_DAYS = 7;     // taslak, bu kadar gündür yarım duruyor
export const MAX_ACTIONS = 5;
export const RECENT_COUNT = 5;
export const ACTIVITY_MONTHS = 6;

const DAY_MS = 86_400_000;

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

export function parseDate(value?: string): Date | null {
  if (!value) return null;
  // Date-only values (`client.validityDate` comes from an <input type="date">)
  // are read as local calendar days — `new Date('2026-08-25')` would parse as
  // UTC midnight and slip a day for anyone west of Greenwich.
  if (DATE_ONLY.test(value)) {
    const [y, m, d] = value.split('-').map(Number);
    return new Date(y, m - 1, d);
  }
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d;
}

/** Whole calendar days from `from` to `to` (negative when `to` is earlier). */
export function daysBetween(from: Date, to: Date): number {
  const a = new Date(from.getTime()); a.setHours(0, 0, 0, 0);
  const b = new Date(to.getTime()); b.setHours(0, 0, 0, 0);
  return Math.round((b.getTime() - a.getTime()) / DAY_MS);
}

/**
 * Large TL sums are abbreviated so a KPI tile never wraps: a bulk project can
 * reach eight digits, which would blow the tile's layout apart.
 */
export function formatCompactTL(value: number): string {
  const v = Math.round(value || 0);
  if (Math.abs(v) >= 1_000_000) {
    return `${(v / 1_000_000).toLocaleString('tr-TR', { maximumFractionDigits: 1 })} mn TL`;
  }
  return `${v.toLocaleString('tr-TR')} TL`;
}

export function formatFullTL(value: number): string {
  return `${Math.round(value || 0).toLocaleString('tr-TR')} TL`;
}

/** "Bugün" / "Dün" / "5 gün önce" / "12 Mar" — for last-touched timestamps. */
export function formatRelativeDay(iso: string | undefined, now: Date): string {
  const d = parseDate(iso);
  if (!d) return '-';
  const days = daysBetween(d, now);
  if (days <= 0) return 'Bugün';
  if (days === 1) return 'Dün';
  if (days < 30) return `${days} gün önce`;
  return d.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' });
}

export interface MonthPoint {
  key: string;   // YYYY-MM
  label: string; // "Ağu"
  count: number;
  total: number;
}

export interface StatusSlice {
  status: ProposalStatus;
  count: number;
  total: number;
}

export type ActionKind = 'expired' | 'expiring' | 'stale_quoted' | 'stale_draft';

export interface ActionItem {
  id: string;
  kind: ActionKind;
  /** Days past expiry / left until expiry / since the last touch, depending on `kind`. */
  days: number;
  proposal: SavedProposalDoc;
}

export interface WelcomeMetrics {
  total: number;
  thisMonth: { count: number; total: number };
  lastMonth: { count: number; total: number };
  /** Percentage change of this month's volume vs. last month's; null when there is no base to compare against. */
  monthDeltaPct: number | null;
  pipeline: { count: number; total: number };
  /** Mean value of the proposals that carry a price — the designer's typical ticket size. */
  averageAmount: number;
  results: { won: number; lost: number; winRate: number | null };
  wonThisYear: { count: number; total: number };
  statusBreakdown: StatusSlice[];
  monthly: MonthPoint[];
  actions: ActionItem[];
  recent: SavedProposalDoc[];
}

const monthKey = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;

const ACTION_PRIORITY: Record<ActionKind, number> = {
  expired: 0,
  expiring: 1,
  stale_quoted: 2,
  stale_draft: 3,
};

/**
 * Picks the single most urgent action for one proposal, or null when it needs
 * nothing. One row per proposal keeps the list scannable — a proposal that is
 * both expiring and stale is still just one phone call.
 */
function actionFor(p: SavedProposalDoc, now: Date): ActionItem | null {
  const updated = parseDate(p.updatedAt) || parseDate(p.createdAt);
  const validity = parseDate(p.client?.validityDate);

  if (p.status === 'quoted' && validity) {
    const daysLeft = daysBetween(now, validity);
    if (daysLeft < 0) return { id: p.id, kind: 'expired', days: -daysLeft, proposal: p };
    if (daysLeft <= EXPIRING_SOON_DAYS) return { id: p.id, kind: 'expiring', days: daysLeft, proposal: p };
  }

  const idle = updated ? daysBetween(updated, now) : 0;

  if (p.status === 'quoted' && idle >= STALE_QUOTED_DAYS) {
    return { id: p.id, kind: 'stale_quoted', days: idle, proposal: p };
  }
  if (p.status === 'draft' && idle >= STALE_DRAFT_DAYS) {
    return { id: p.id, kind: 'stale_draft', days: idle, proposal: p };
  }
  return null;
}

/**
 * Turns the signed-in designer's own proposals into every number the welcome
 * screen shows. Pure and `now`-injectable so the whole dashboard is testable.
 */
export function buildWelcomeMetrics(proposals: SavedProposalDoc[], now: Date = new Date()): WelcomeMetrics {
  const thisMonthKey = monthKey(now);
  const lastMonthDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const lastMonthKey = monthKey(lastMonthDate);

  const thisMonth = { count: 0, total: 0 };
  const lastMonth = { count: 0, total: 0 };
  const pipeline = { count: 0, total: 0 };
  const wonThisYear = { count: 0, total: 0 };

  const byStatus: Record<ProposalStatus, { count: number; total: number }> = {
    draft: { count: 0, total: 0 },
    quoted: { count: 0, total: 0 },
    won: { count: 0, total: 0 },
    lost: { count: 0, total: 0 },
  };

  // Trailing ACTIVITY_MONTHS buckets, oldest first, current month last.
  const monthly: MonthPoint[] = [];
  const monthIndex = new Map<string, MonthPoint>();
  for (let i = ACTIVITY_MONTHS - 1; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const point: MonthPoint = {
      key: monthKey(d),
      label: d.toLocaleDateString('tr-TR', { month: 'short' }),
      count: 0,
      total: 0,
    };
    monthly.push(point);
    monthIndex.set(point.key, point);
  }

  const actions: ActionItem[] = [];
  let pricedCount = 0;
  let pricedTotal = 0;

  proposals.forEach(p => {
    const amount = p.totalAmount || 0;
    const status: ProposalStatus = (p.status || 'draft') as ProposalStatus;
    const created = parseDate(p.createdAt);

    if (byStatus[status]) {
      byStatus[status].count += 1;
      byStatus[status].total += amount;
    }

    if (amount > 0) { pricedCount += 1; pricedTotal += amount; }

    if (created) {
      const key = monthKey(created);
      if (key === thisMonthKey) { thisMonth.count += 1; thisMonth.total += amount; }
      if (key === lastMonthKey) { lastMonth.count += 1; lastMonth.total += amount; }
      const bucket = monthIndex.get(key);
      if (bucket) { bucket.count += 1; bucket.total += amount; }
    }

    if (status === 'quoted') { pipeline.count += 1; pipeline.total += amount; }

    if (status === 'won') {
      // "Kazanılan" is dated by the last touch — that is when the deal closed,
      // not when the proposal was first drafted.
      const closed = parseDate(p.updatedAt) || created;
      if (closed && closed.getFullYear() === now.getFullYear()) {
        wonThisYear.count += 1;
        wonThisYear.total += amount;
      }
    }

    const action = actionFor(p, now);
    if (action) actions.push(action);
  });

  actions.sort((a, b) =>
    ACTION_PRIORITY[a.kind] - ACTION_PRIORITY[b.kind] ||
    b.days - a.days ||
    (b.proposal.totalAmount || 0) - (a.proposal.totalAmount || 0)
  );

  const recent = [...proposals]
    .sort((a, b) => {
      const at = parseDate(a.updatedAt || a.createdAt)?.getTime() ?? 0;
      const bt = parseDate(b.updatedAt || b.createdAt)?.getTime() ?? 0;
      return bt - at;
    })
    .slice(0, RECENT_COUNT);

  const decided = byStatus.won.count + byStatus.lost.count;

  return {
    total: proposals.length,
    thisMonth,
    lastMonth,
    monthDeltaPct: lastMonth.total > 0
      ? Math.round(((thisMonth.total - lastMonth.total) / lastMonth.total) * 100)
      : null,
    pipeline,
    averageAmount: pricedCount > 0 ? Math.round(pricedTotal / pricedCount) : 0,
    results: {
      won: byStatus.won.count,
      lost: byStatus.lost.count,
      winRate: decided > 0 ? Math.round((byStatus.won.count / decided) * 100) : null,
    },
    wonThisYear,
    statusBreakdown: STATUS_ORDER.map(status => ({ status, ...byStatus[status] })),
    monthly,
    actions,
    recent,
  };
}

/** One-line summary under the greeting — the "what should I care about today" sentence. */
export function buildHeadline(m: WelcomeMetrics): string {
  if (m.total === 0) {
    return 'Henüz kayıtlı teklifin yok. İlk teklifini oluşturduğunda özetin burada birikmeye başlayacak.';
  }

  const parts: string[] = [
    m.thisMonth.count > 0
      ? `Bu ay ${m.thisMonth.count} teklif hazırladın`
      : 'Bu ay henüz yeni bir teklif hazırlamadın',
  ];
  if (m.pipeline.count > 0) parts.push(`${m.pipeline.count} teklif müşteri yanıtı bekliyor`);
  if (m.actions.length > 0) parts.push(`${m.actions.length} teklif için aksiyon zamanı`);
  if (m.pipeline.count === 0 && m.actions.length === 0) parts.push('takipte bekleyen bir işin yok');

  return `${parts.join(', ')}.`;
}


/**
 * A snapshot of the retail editor's current in-memory state, taken from
 * AppContext — used only to decide whether the "Kaldığın yerden devam et"
 * resume card is worth showing on the welcome screen.
 */
export interface RetailEditorSnapshot {
  clientName: string;
  model: string;
  specItemCount: number;
  hasPricing: boolean;
}

/**
 * True when the retail editor holds real, unsaved-looking work — as opposed
 * to the pristine blank state a brand-new proposal starts from. Deliberately
 * ORs several weak signals (client name, model, spec items, any entered
 * price) since a designer might fill these in any order, and any one of them
 * being non-default means there is something to lose by not surfacing it.
 */
export function hasResumableDraft(snapshot: RetailEditorSnapshot): boolean {
  return Boolean(
    snapshot.clientName.trim() ||
    snapshot.model ||
    snapshot.specItemCount > 0 ||
    snapshot.hasPricing
  );
}
